// What the editor acts on: one clip of the draft, or several (live's ⇧-click and ⌘A, #267 part
// 2b), or a transition (#240, chosen alone). `id` is the one the Parameters show (the first
// chosen); `ids` holds every chosen clip, `id` first, in the order they were chosen.
import type { Timeline } from "./timeline";

export interface Selection {
  id: string;
  ids: readonly string[];
}

/** One clip chosen alone. */
export function only(id: string): Selection {
  return { id, ids: [id] };
}

/** Whether a clip is among the chosen. */
export function isChosen(selection: Selection | null, id: string): boolean {
  return selection?.ids.includes(id) ?? false;
}

/**
 * Live's ⇧-click: a clip not chosen joins the selection; a chosen one leaves it (the next chosen
 * becomes the one the Parameters show), and the last one leaving leaves nothing chosen.
 */
export function toggled(selection: Selection | null, id: string): Selection | null {
  if (selection === null) return only(id);
  if (!selection.ids.includes(id)) return { id: selection.id, ids: [...selection.ids, id] };
  const ids = selection.ids.filter((each) => each !== id);
  const [first] = ids;
  return first === undefined ? null : { id: first, ids };
}

/**
 * The chosen clips, or null while a transition is chosen: it is chosen alone, so a ⇧-click on a
 * clip then starts a selection afresh.
 */
export function clipsChosen(selection: Selection | null, timeline: Timeline): Selection | null {
  return selection !== null && timeline.transitions.some((each) => each.id === selection.id) ? null : selection;
}

/** Live's ⌘A: every clip, the one shown first if it was chosen, else the timeline's first. */
export function everything(timeline: Timeline, selection: Selection | null): Selection | null {
  const ids = timeline.clips.map((clip) => clip.id);
  const [first] = ids;
  if (first === undefined) return null;
  const shown = selection !== null && ids.includes(selection.id) ? selection.id : first;
  return { id: shown, ids: [shown, ...ids.filter((each) => each !== shown)] };
}

/** The selection's clips and transition still in the timeline, else `null`. */
export function stillSelected(selection: Selection | null, timeline: Timeline): Selection | null {
  if (selection === null) return null;
  const known = new Set([...timeline.clips, ...timeline.transitions].map((each) => each.id));
  const ids = selection.ids.filter((id) => known.has(id));
  if (ids.length === selection.ids.length) return selection;
  const [first] = ids;
  if (first === undefined) return null;
  return { id: ids.includes(selection.id) ? selection.id : first, ids };
}
